import Vue from "vue";
import VueRouter from "vue-router";
import HomeView from "../views/HomeView.vue";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "HomeView",
    component: HomeView,
    meta: {
      title: "Private Theater & Celebration Space",
      description:
        "Happy Harbour is a private theater and celebration space in Bangalore for intimate parties, private screenings, birthdays, and corporate gatherings.",
    },
  },
  {
    path: "/book-now",
    name: "BookNow",
    props: true,
    component: () =>
      import(/* webpackChunkName: "booknow" */ "../views/BookNow.vue"),
    meta: {
      title: "Book Private Theater",
      description:
        "Reserve Happy Harbour's private theater and celebration venue for private screenings, parties, anniversaries, and special events.",
    },
  },
  // 1. Fixed Duplicate Name: Default /book-online goes to BookNow view
  {
    path: "/book-online",
    name: "BookOnlineDefault",
    props: true,
    component: () =>
      import(/* webpackChunkName: "booknow" */ "../views/BookNow.vue"),
    meta: {
      title: "Book Celebration Space",
      description:
        "Choose your Happy Harbour celebration space and book your private theater event online today.",
    },
  },
  // 2. Fixed Duplicate Name: Location specific view
  {
    path: "/book-online/:location",
    name: "BookOnline", // Kept this as the main 'BookOnline'
    props: true,
    component: () =>
      import(/* webpackChunkName: "bookonline" */ "../views/BookOnline.vue"),
    meta: {
      title: "Private Theater Booking",
      description:
        "Select your Happy Harbour location and book a private theater or celebration event online.",
    },
  },
  // 3. Fixed Duplicate Name: Space details view
  // {
  //   path: "/book-online/:location",
  //   name: "BookOnline",
  //   props: true,
  //   component: () =>
  //     import(/* webpackChunkName: "bookonline" */ "../views/BookOnline.vue"),
  // },
  {
    path: "/booking-details/:location/:planid/:slotid",
    name: "BookingDetails",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "bookingdetails" */ "../views/BookingDetails.vue"
      ),
    meta: {
      title: "Reservation Details",
      description:
        "Review your Happy Harbour private theater reservation details and finalize your celebration event booking.",
    },
  },
  {
    path: "/privacy-policy",
    name: "PrivacyPolicyView",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "privacypolicy" */ "../views/PrivacyPolicyView.vue"
      ),
    meta: {
      title: "Privacy Policy",
      description:
        "Read Happy Harbour's privacy policy for customer data and booking information at our private theater and celebration space.",
    },
  },
  {
    path: "/refund-policy",
    name: "RefundPolicyView",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "refundpolicy" */ "../views/RefundPolicyView.vue"
      ),
    meta: {
      title: "Refund Policy",
      description:
        "Learn Happy Harbour's refund and cancellation policy for private theater bookings and celebration events.",
    },
  },
  {
    path: "/terms-and-conditions",
    name: "TermsAndConditionsView",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "termsandconditions" */ "../views/TermsAndConditionsView.vue"
      ),
    meta: {
      title: "Terms & Conditions",
      description:
        "Review Happy Harbour's terms and conditions for booking the private theater and celebration venue.",
    },
  },
  // Booking Status (Success/Failure)
  {
    path: "/booking/:id",
    name: "BookingStatus",
    props: true,
    component: () =>
      import(/* webpackChunkName: "booking" */ "../views/BookingView.vue"),
    meta: {
      title: "Booking Status",
      description:
        "View the status of your Happy Harbour private theater or celebration venue booking and payment confirmation.",
    },
  },
  // Alias for Payment Gateway callbacks if needed
  {
    path: "/bookingresponse/:id",
    name: "BookingResponse",
    props: true,
    component: () =>
      import(/* webpackChunkName: "booking" */ "../views/BookingView.vue"),
    meta: {
      title: "Booking Status",
      description:
        "View the status of your Happy Harbour private theater or celebration venue booking confirmation.",
    },
  },
  {
    path: "/space-details/:location/:space",
    name: "SpaceDetails",
    component: () =>
      import(/* webpackChunkName: "booknow" */ "../views/SpaceDetails.vue"), // Redirects to BookNow as fallback
    meta: {
      title: "Venue Details",
      description:
        "Discover Happy Harbour's private theater and celebration space details, amenities, and booking availability.",
    },
  },
  // Friendly location URLs like `/rr-nagar` should load the BookOnline view
  {
    path: "/:location",
    name: "BookOnlineAlias",
    props: true,
    component: () =>
      import(/* webpackChunkName: "bookonline" */ "../views/BookOnline.vue"),
    meta: {
      title: "Book Private Theater",
      description:
        "Find your Happy Harbour private theater location and complete your celebration space booking online.",
    },
  },
  // 404 / Catch-all
  {
    path: "*",
    name: "PageNotFound",
    component: () =>
      import(/* webpackChunkName: "booknow" */ "../views/BookNow.vue"), // Redirects to BookNow as fallback
    meta: {
      title: "Page Not Found",
      description:
        "Page not found. Return to Happy Harbour and explore private theater and celebration space booking options.",
    },
  },
];

const router = new VueRouter({
  routes,
  mode: "history",
  base: "/",

  // Nifty Feature: Handles scrolling automatically
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        selector: to.hash,
        behavior: "smooth",
        offset: { x: 0, y: 80 }, // Offset for sticky header
      };
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Optional: Dynamic Title Changer and SEO metadata updater
function getAbsoluteUrl(path) {
  try {
    return window.location.origin + path;
  } catch (error) {
    return path;
  }
}

function updateMetaTag(attrName, attrValue, content) {
  let selector = `meta[${attrName}="${attrValue}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function updateCanonicalLink(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

router.beforeEach((to, from, next) => {
  const routeTitle = to.meta.title ? `Happy Harbour | ${to.meta.title}` : "Happy Harbour";
  const routeDescription =
    to.meta.description || "Happy Harbour booking and rental platform.";
  const routeUrl = getAbsoluteUrl(to.fullPath);

  document.title = routeTitle;
  updateMetaTag("name", "description", routeDescription);
  updateMetaTag("property", "og:title", routeTitle);
  updateMetaTag("property", "og:description", routeDescription);
  updateMetaTag("property", "og:url", routeUrl);
  updateMetaTag("name", "twitter:title", routeTitle);
  updateMetaTag("name", "twitter:description", routeDescription);
  updateMetaTag("name", "twitter:url", routeUrl);
  updateCanonicalLink(routeUrl);

  next();
});

export default router;
